import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Kanban, Users, ChevronLeft, ChevronRight, Flame, LogOut, X, SearchCheck, ClipboardList, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/pipeline", icon: Kanban, label: "Pipeline" },
  { to: "/leads", icon: Users, label: "Leads" },
  { to: "/activities", icon: ClipboardList, label: "Activities" },
  { to: "/certificates", icon: ShieldCheck, label: "Certificates" },
  { to: "/scraper", icon: SearchCheck, label: "Lead Finder" },
];

export const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose, user, onLogout }) => {
  const location = useLocation();

  const sidebarContent = (isMobile) => (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center gap-3 px-4 border-b border-border">
        <div className="w-9 h-9 rounded-sm bg-blue-500 flex items-center justify-center flex-shrink-0">
          <Flame className="w-5 h-5 text-white" />
        </div>
        {(isMobile || !collapsed) && (
          <div className="animate-fade-in flex-1 min-w-0">
            <h1 className="text-lg font-bold tracking-tight text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              GASFLOW PRO
            </h1>
            <p className="text-[10px] tracking-widest uppercase text-muted-foreground -mt-0.5">CRM Dashboard</p>
          </div>
        )}
        {isMobile && (
          <button onClick={onMobileClose} className="p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-white/5" data-testid="mobile-menu-close">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={isMobile ? onMobileClose : undefined}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-150",
                isActive
                  ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
              )}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              {(isMobile || !collapsed) && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div className="px-3 pb-4 space-y-2">
        {user && (isMobile || !collapsed) && (
          <div className="flex items-center gap-2.5 px-3 py-2 rounded-sm bg-white/[0.02] border border-white/5">
            <Avatar className="w-7 h-7 flex-shrink-0">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="text-[10px] bg-blue-500/20 text-blue-400">{user.name?.[0] || "U"}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground truncate">{user.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        )}
        {user && collapsed && !isMobile && (
          <div className="flex justify-center py-2">
            <Avatar className="w-7 h-7">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="text-[10px] bg-blue-500/20 text-blue-400">{user.name?.[0] || "U"}</AvatarFallback>
            </Avatar>
          </div>
        )}

        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 rounded-sm text-xs text-red-400/70 hover:text-red-400 hover:bg-red-500/5 transition-colors duration-150 border border-transparent hover:border-red-500/10",
            collapsed && !isMobile && "justify-center"
          )}
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          {(isMobile || !collapsed) && <span className="tracking-wider uppercase">Sign Out</span>}
        </button>

        {/* Desktop collapse toggle */}
        {!isMobile && (
          <button
            data-testid="sidebar-toggle"
            onClick={onToggle}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-xs text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors duration-150 border border-transparent hover:border-border"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            {!collapsed && <span className="tracking-wider uppercase">Collapse</span>}
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex fixed left-0 top-0 h-screen bg-card border-r border-border flex-col z-40"
        style={{ width: collapsed ? '72px' : '240px', transition: 'width 0.2s ease' }}
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          onClick={onMobileClose}
          data-testid="mobile-overlay"
        />
      )}

      {/* Mobile Drawer */}
      <aside
        data-testid="mobile-sidebar"
        className={cn(
          "md:hidden fixed left-0 top-0 h-screen w-[260px] bg-card border-r border-border flex flex-col z-50",
          "transition-transform duration-200 ease-in-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {sidebarContent(true)}
      </aside>
    </>
  );
};
